
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Users, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';

interface PublishedSuggestion {
  id: string;
  poll_id: string;
  status: string;
  total_matches: number;
  total_players: number;
  created_at: string;
  poll: {
    id: string;
    title: string;
    proposed_date: string;
    group_id: string;
    groups: {
      name: string;
    };
  };
}

export const SuggestedPairingsWidget: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: publishedSuggestions, isLoading } = useQuery({
    queryKey: ['published-suggestions', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // First get the group IDs where user is a member
      const { data: memberGroups, error: memberError } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id);

      if (memberError) throw memberError;
      
      const groupIds = memberGroups?.map(mg => mg.group_id) || [];
      if (groupIds.length === 0) return [];

      // Get sessions with 'published' status (suggestions ready)
      const { data: sessions, error: sessionError } = await supabase
        .from('suggested_match_sessions')
        .select(`
          id,
          poll_id,
          status,
          total_matches,
          total_players,
          created_at,
          poll:group_availability_polls!inner(
            id,
            title,
            proposed_date,
            group_id,
            groups!inner(name)
          )
        `)
        .eq('status', 'published')
        .in('poll.group_id', groupIds)
        .order('created_at', { ascending: false })
        .limit(10);

      if (sessionError) throw sessionError;
      if (!sessions || sessions.length === 0) return [];

      // Filter out sessions that already have scheduled matches
      const sessionIds = sessions.map(s => s.id);
      const { data: scheduledMatches } = await supabase
        .from('published_matches')
        .select('suggested_match_id')
        .in('suggested_match_id', sessionIds.flatMap(sid => 
          // We need to check by poll_id since suggested_match_id references individual matches
          []
        ));
      
      // Get poll_ids that have scheduled matches
      const pollIds = sessions.map(s => s.poll_id);
      const { data: scheduledByPoll } = await supabase
        .from('published_matches')
        .select('poll_id')
        .in('poll_id', pollIds)
        .eq('status', 'scheduled');
      
      const pollsWithScheduledMatches = new Set(scheduledByPoll?.map(m => m.poll_id) || []);
      
      // Filter out sessions whose polls already have scheduled matches
      const filteredSessions = sessions.filter(s => !pollsWithScheduledMatches.has(s.poll_id));
      
      return filteredSessions.slice(0, 3);
    },
    enabled: !!user
  });

  const handleViewSuggestions = (suggestion: PublishedSuggestion) => {
    // Navigate to the specific group's polls tab with the poll opened
    navigate(`/groups/${suggestion.poll.group_id}?tab=polls&openPoll=${suggestion.poll_id}`);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center">
            <Trophy className="h-4 w-4 mr-2 text-primary" />
            Suggested Pairings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse bg-muted rounded-lg h-16"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!publishedSuggestions || publishedSuggestions.length === 0) {
    return null; // Don't show the widget if there are no published suggestions
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center">
              <Trophy className="h-4 w-4 mr-2 text-primary" />
              Suggested Pairings
            </CardTitle>
            {publishedSuggestions.length > 2 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/groups')}
                className="text-primary hover:text-primary/90 text-xs"
              >
                View All <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {publishedSuggestions.slice(0, 2).map((suggestion) => (
              <Card
                key={suggestion.id}
                className="border hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => handleViewSuggestions(suggestion)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate">
                        {suggestion.poll.title}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate">
                        {suggestion.poll.groups.name}
                      </p>
                    </div>
                    <Badge variant="outline" className="ml-2 text-xs">
                      Published
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                    <div className="flex items-center gap-1">
                      <Trophy className="h-3 w-3" />
                      <span>{suggestion.total_matches} matches</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{suggestion.total_players} players</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDistanceToNow(new Date(suggestion.poll.proposed_date), { addSuffix: true })}</span>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between h-8 text-xs"
                  >
                    View Suggested Matches
                    <ArrowRight className="h-3 w-3" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
